import { useState, useRef, useEffect } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Vehicle, Tag, Dealership, ProgressStatus,
  VehicleClass, HandlingLogEntry, AuditLogEntry,
} from '../types';
import {
  VEHICLE_CLASSES, TIER_RULES, PROGRESS_STATUSES, CATEGORIES,
} from '../types';
import { vehiclesApi } from '../api/vehicles';
import { vehicleImageSrc, formatDate, formatMinutes } from '../utils/helpers';
import StatusBadge from './StatusBadge';
import TagPill from './TagPill';

interface Props {
  vehicle: Vehicle | null; // null = add new
  onClose: () => void;
  tags: Tag[];
  dealerships: Dealership[];
}

type Section = 'general' | 'performance' | 'server' | 'progress' | 'tags' | 'log' | 'image' | 'audit';

export default function VehicleModal({ vehicle, onClose, tags, dealerships }: Props) {
  const isNew = vehicle === null;
  const qc = useQueryClient();
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [openSection, setOpenSection] = useState<Section>('general');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [conflictNote, setConflictNote] = useState<'dealership' | 'boosting' | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageTab, setImageTab] = useState<'file' | 'clipboard' | 'url'>('file');
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const confirmRef = useRef<HTMLDivElement>(null);
  const [newLogEntry, setNewLogEntry] = useState({ notes: '', time_spent_minutes: 0 });
  const [auditNote, setAuditNote] = useState('');
  const conflictRef = useRef<HTMLDivElement>(null);

  // Form state
  const [form, setForm] = useState<Partial<Vehicle> & { tag_ids: number[] }>(() => ({
    name: vehicle?.name ?? '',
    spawncode: vehicle?.spawncode ?? '',
    brand: vehicle?.brand ?? '',
    category: vehicle?.category ?? '',
    vehicle_class: vehicle?.vehicle_class ?? 'S',
    price: vehicle?.price ?? undefined,
    link: vehicle?.link ?? '',
    doors: vehicle?.doors ?? undefined,
    seats: vehicle?.seats ?? undefined,
    gears: vehicle?.gears ?? undefined,
    top_speed: vehicle?.top_speed ?? undefined,
    zero_to_sixty: vehicle?.zero_to_sixty ?? undefined,
    zero_to_hundred: vehicle?.zero_to_hundred ?? undefined,
    tiers: vehicle?.tiers ?? '',
    dealership_id: vehicle?.dealership_id ?? undefined,
    progress: vehicle?.progress ?? 'To Do',
    boosting_exclusive: vehicle?.boosting_exclusive ?? 0,
    rp_exclusive: vehicle?.rp_exclusive ?? 0,
    tag_ids: vehicle?.tags?.map(t => t.id) ?? [],
  }));

  // Snapshot of form at mount — used for dirty detection
  const initialFormRef = useRef(form);

  // Handling log query
  const { data: logData, refetch: refetchLog } = useQuery({
    queryKey: ['handling-log', vehicle?.id],
    queryFn: () => vehiclesApi.getHandlingLog(vehicle!.id),
    enabled: !!vehicle?.id && openSection === 'log',
  });

  // Audit log query
  const { data: auditData } = useQuery({
    queryKey: ['audit-log', vehicle?.id],
    queryFn: () => vehiclesApi.getAuditLog(vehicle!.id),
    enabled: !!vehicle?.id && openSection === 'audit',
  });

  const saveMutation = useMutation({
    mutationFn: (data: typeof form) =>
      isNew
        ? vehiclesApi.create({ ...data, audit_note: auditNote } as Parameters<typeof vehiclesApi.create>[0])
        : vehiclesApi.update(vehicle!.id, { ...data, audit_note: auditNote }),
    onSuccess: async (data) => {
      // If creating new and there's a queued image, attach it now that we have an ID
      if (isNew && data && 'vehicle' in data) {
        const newId = (data as { vehicle: { id: number } }).vehicle.id;
        try {
          if (imageFile) {
            await vehiclesApi.uploadImage(newId, imageFile);
          } else if (imageUrlInput.trim()) {
            await vehiclesApi.setImageUrl(newId, imageUrlInput.trim());
          }
        } catch { /* image failure shouldn't block closing */ }
      }
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => vehiclesApi.delete(vehicle!.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['vehicles'] }); onClose(); },
  });

  const addLogMutation = useMutation({
    mutationFn: () => vehiclesApi.addHandlingLog(vehicle!.id, newLogEntry),
    onSuccess: () => { refetchLog(); setNewLogEntry({ notes: '', time_spent_minutes: 0 }); },
  });

  const uploadMutation = useMutation({
    mutationFn: () => vehiclesApi.uploadImage(vehicle!.id, imageFile!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['vehicles'] }); setImageFile(null); setImagePreview(null); },
  });

  const setImageUrlMutation = useMutation({
    mutationFn: () => vehiclesApi.setImageUrl(vehicle!.id, imageUrlInput.trim()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['vehicles'] }); setImageUrlInput(''); },
  });

  // GSAP — modal entrance
  useGSAP(() => {
    gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power2.out' });
    gsap.fromTo(panelRef.current, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.35, ease: 'power3.out' });
  }, []);

  // GSAP — conflict note
  useGSAP(() => {
    if (conflictRef.current) {
      if (conflictNote) {
        gsap.fromTo(conflictRef.current, { height: 0, opacity: 0 }, { height: 'auto', opacity: 1, duration: 0.25 });
      } else {
        gsap.to(conflictRef.current, { height: 0, opacity: 0, duration: 0.2 });
      }
    }
  }, [conflictNote]);

  // Delete shake — runs after re-render so the Confirm button is in the DOM
  const deleteRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (deleteConfirm && deleteRef.current) {
      gsap.fromTo(deleteRef.current, { x: -8 }, { x: 0, duration: 0.4, ease: 'elastic.out(1,0.3)' });
    }
  }, [deleteConfirm]);

  // GSAP — close confirm dialog entrance
  useGSAP(() => {
    if (showCloseConfirm && confirmRef.current) {
      gsap.fromTo(
        confirmRef.current,
        { scale: 0.82, opacity: 0, y: -14 },
        { scale: 1, opacity: 1, y: 0, duration: 0.38, ease: 'back.out(1.8)' },
      );
    }
  }, [showCloseConfirm]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showCloseConfirm) dismissConfirm();
        else requestClose();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); handleSave(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const close = () => {
    gsap.to(overlayRef.current, { opacity: 0, duration: 0.2 });
    gsap.to(panelRef.current, { y: 20, opacity: 0, duration: 0.2, onComplete: onClose });
  };

  const isDirty = (): boolean => {
    if (imageFile !== null || imageUrlInput.trim() !== '') return true;
    if (auditNote.trim() !== '') return true;
    const i = initialFormRef.current;
    const sortIds = (ids: number[]) => [...ids].sort().join(',');
    return (
      form.name            !== i.name            ||
      form.spawncode       !== i.spawncode        ||
      form.brand           !== i.brand            ||
      form.category        !== i.category         ||
      form.vehicle_class   !== i.vehicle_class    ||
      form.price           !== i.price            ||
      form.link            !== i.link             ||
      form.doors           !== i.doors            ||
      form.seats           !== i.seats            ||
      form.gears           !== i.gears            ||
      form.top_speed       !== i.top_speed        ||
      form.zero_to_sixty   !== i.zero_to_sixty    ||
      form.zero_to_hundred !== i.zero_to_hundred  ||
      form.tiers           !== i.tiers            ||
      form.dealership_id   !== i.dealership_id    ||
      form.progress        !== i.progress         ||
      form.boosting_exclusive !== i.boosting_exclusive ||
      form.rp_exclusive    !== i.rp_exclusive     ||
      sortIds(form.tag_ids) !== sortIds(i.tag_ids)
    );
  };

  const requestClose = () => {
    if (isDirty()) setShowCloseConfirm(true);
    else close();
  };

  const dismissConfirm = () => {
    if (!confirmRef.current) { setShowCloseConfirm(false); return; }
    gsap.to(confirmRef.current, {
      scale: 0.88, opacity: 0, y: -8, duration: 0.2, ease: 'power2.in',
      onComplete: () => setShowCloseConfirm(false),
    });
  };

  const confirmDiscard = () => {
    if (!confirmRef.current) { setShowCloseConfirm(false); close(); return; }
    gsap.to(confirmRef.current, {
      scale: 0.88, opacity: 0, duration: 0.15, ease: 'power2.in',
      onComplete: () => { setShowCloseConfirm(false); close(); },
    });
  };

  const handleSave = () => saveMutation.mutate(form);

  const setField = (key: keyof typeof form, value: unknown) => {
    setForm(f => {
      const next = { ...f, [key]: value };
      // Conflict enforcement
      if (key === 'dealership_id' && value) {
        next.boosting_exclusive = 0;
        setConflictNote('dealership');
        setTimeout(() => setConflictNote(null), 3000);
      }
      if (key === 'boosting_exclusive' && value) {
        next.dealership_id = undefined;
        setConflictNote('boosting');
        setTimeout(() => setConflictNote(null), 3000);
      }
      return next;
    });
  };

  const toggleTier = (tier: string) => {
    const current = (form.tiers ?? '').split(',').filter(Boolean);
    const next = current.includes(tier) ? current.filter(t => t !== tier) : [...current, tier];
    setField('tiers', next.join(','));
  };

  const toggleTag = (id: number) => {
    setForm(f => {
      const ids = f.tag_ids ?? [];
      return { ...f, tag_ids: ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id] };
    });
  };

  const availableTiers = TIER_RULES[form.vehicle_class as VehicleClass] ?? [];
  const selectedTiers = (form.tiers ?? '').split(',').filter(Boolean);
  const selectedTagObjs = tags.filter(t => form.tag_ids.includes(t.id));

  const sectionBtn = (id: Section, label: string) => (
    <button
      key={id}
      onClick={() => setOpenSection(id)}
      className={`px-3 py-1.5 font-heading uppercase text-xs tracking-wide border-b-2 transition-colors ${
        openSection === id ? 'border-primary text-primary' : 'border-transparent'
      }`}
    style={openSection !== id ? { color: 'var(--text-muted)' } : undefined}
    >
      {label}
    </button>
  );

  return (
    <div ref={overlayRef} className="modal-overlay" onClick={(e) => e.target === overlayRef.current && requestClose()}>
      <div
        ref={panelRef}
        className="w-full max-w-3xl max-h-[90vh] flex flex-col shadow-modal relative"
        style={{ background: 'var(--card)', willChange: 'transform, opacity' }}
      >
        {/* Header */}
        <div className="bg-dark text-white px-5 py-3 flex items-center justify-between flex-shrink-0">
          <span className="font-heading text-lg tracking-wide">
            {isNew ? 'Add Vehicle' : (vehicle!.name ?? vehicle!.spawncode)}
          </span>
          <button onClick={requestClose} className="text-white/60 hover:text-white text-xl leading-none">×</button>
        </div>

        {/* Section tabs */}
        <div className="flex gap-0 border-b px-4 overflow-x-auto flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
          {sectionBtn('general', 'General')}
          {sectionBtn('performance', 'Performance')}
          {sectionBtn('server', 'Server')}
          {sectionBtn('progress', 'Progress')}
          {sectionBtn('tags', 'Tags')}
          {!isNew && sectionBtn('log', 'Handling Log')}
          {sectionBtn('image', 'Image')}
          {!isNew && sectionBtn('audit', 'Audit')}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4">

          {/* ── GENERAL ── */}
          {openSection === 'general' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Name</label>
                <input className="input" value={form.name ?? ''} onChange={e => setField('name', e.target.value)} />
              </div>
              <div>
                <label className="label">Spawncode *</label>
                <input className="input" value={form.spawncode ?? ''} onChange={e => setField('spawncode', e.target.value)} required />
              </div>
              <div>
                <label className="label">Brand</label>
                <input className="input" value={form.brand ?? ''} onChange={e => setField('brand', e.target.value)} />
              </div>
              <div>
                <label className="label">Category</label>
                <select className="input" value={form.category ?? ''} onChange={e => setField('category', e.target.value)}>
                  <option value="">— Select —</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Vehicle Class *</label>
                <select className="input" value={form.vehicle_class ?? 'S'} onChange={e => setField('vehicle_class', e.target.value)}>
                  {VEHICLE_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Price (£)</label>
                <input className="input" type="number" value={form.price ?? ''} onChange={e => setField('price', e.target.value ? parseInt(e.target.value) : null)} />
              </div>
              <div className="col-span-2">
                <label className="label">Source Link</label>
                <div className="flex gap-2">
                  <input className="input flex-1" value={form.link ?? ''} onChange={e => setField('link', e.target.value)} placeholder="https://..." />
                  {form.link && (
                    <a href={form.link} target="_blank" rel="noreferrer" className="px-3 py-2 bg-dark text-white text-xs font-body border-l-2 border-primary hover:bg-primary transition-colors">
                      Open ↗
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── PERFORMANCE ── */}
          {openSection === 'performance' && (
            <div className="grid grid-cols-3 gap-4">
              {[
                { key: 'doors', label: 'Doors' },
                { key: 'seats', label: 'Seats' },
                { key: 'gears', label: 'Gears' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="label">{label}</label>
                  <input className="input" type="number" value={(form as Record<string, unknown>)[key] as number ?? ''} onChange={e => setField(key as keyof typeof form, e.target.value ? parseInt(e.target.value) : null)} />
                </div>
              ))}
              <div>
                <label className="label">Top Speed (mph)</label>
                <input className="input" type="number" step="0.1" value={form.top_speed ?? ''} onChange={e => setField('top_speed', e.target.value ? parseFloat(e.target.value) : null)} />
              </div>
              <div>
                <label className="label">0–60 (s)</label>
                <input className="input" type="number" step="0.1" value={form.zero_to_sixty ?? ''} onChange={e => setField('zero_to_sixty', e.target.value ? parseFloat(e.target.value) : null)} />
              </div>
              <div>
                <label className="label">0–100 (s)</label>
                <input className="input" type="number" step="0.1" value={form.zero_to_hundred ?? ''} onChange={e => setField('zero_to_hundred', e.target.value ? parseFloat(e.target.value) : null)} />
              </div>
            </div>
          )}

          {/* ── SERVER DETAILS ── */}
          {openSection === 'server' && (
            <div className="space-y-5">
              {/* Tiers */}
              <div>
                <label className="label">Tiers</label>
                {availableTiers.length === 0 ? (
                  <p className="text-text-muted text-sm font-body">No tiers for this class.</p>
                ) : (
                  <div className="flex gap-2 flex-wrap">
                    {availableTiers.map(tier => (
                      <button
                        key={tier}
                        onClick={() => toggleTier(tier)}
                        className={`px-3 py-1 text-sm font-heading uppercase border transition-colors ${
                          selectedTiers.includes(tier) ? 'bg-dark text-white border-dark' : ''
                        }`}
                        style={!selectedTiers.includes(tier) ? { background: 'var(--card)', color: 'var(--text)', borderColor: 'var(--border-strong)' } : undefined}
                      >
                        {tier}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Conflict note */}
              <div ref={conflictRef} className="overflow-hidden" style={{ height: 0 }}>
                <div className="border border-primary bg-red-50 px-3 py-2 text-primary text-xs font-body">
                  ⚠ A vehicle cannot be both dealership-listed and Boosting Exclusive.
                </div>
              </div>

              {/* Dealership */}
              <div className={form.boosting_exclusive ? 'opacity-40 pointer-events-none' : ''}>
                <label className="label">
                  Dealership
                  {form.boosting_exclusive ? <span className="ml-2 line-through text-primary">Not available (Boosting Exclusive)</span> : ''}
                </label>
                <select className="input" value={form.dealership_id ?? ''} onChange={e => setField('dealership_id', e.target.value ? parseInt(e.target.value) : undefined)}>
                  <option value="">— None —</option>
                  {dealerships.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>

              {/* Checkboxes */}
              <div className="grid grid-cols-2 gap-4">
                <label className={`flex items-center gap-2 cursor-pointer ${form.dealership_id ? 'opacity-40 pointer-events-none' : ''}`}>
                  <input
                    type="checkbox"
                    checked={form.boosting_exclusive === 1}
                    onChange={e => setField('boosting_exclusive', e.target.checked ? 1 : 0)}
                    className="w-4 h-4 accent-primary"
                  />
                  <span className="font-body text-sm">Boosting Exclusive</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.rp_exclusive === 1}
                    onChange={e => setField('rp_exclusive', e.target.checked ? 1 : 0)}
                    className="w-4 h-4 accent-primary"
                  />
                  <span className="font-body text-sm">RP Exclusive</span>
                </label>
              </div>
            </div>
          )}

          {/* ── PROGRESS ── */}
          {openSection === 'progress' && (
            <div className="space-y-3">
              <label className="label">Progress Status</label>
              <div className="flex flex-col gap-2">
                {PROGRESS_STATUSES.map(s => (
                  <button
                    key={s}
                    onClick={() => setField('progress', s)}
                    className={`flex items-center gap-3 px-4 py-2.5 border text-left transition-colors ${
                      form.progress === s ? 'border-dark' : ''
                    }`}
                    style={form.progress === s ? { background: 'var(--surface)' } : { borderColor: 'var(--border)' }}
                  >
                    <StatusBadge status={s as ProgressStatus} small />
                    <span className="font-body text-sm">{s}</span>
                    {form.progress === s && <span className="ml-auto" style={{ color: 'var(--text)' }}>✓</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── TAGS ── */}
          {openSection === 'tags' && (
            <div className="space-y-4">
              <div>
                <label className="label">Selected Tags</label>
                <div className="flex flex-wrap gap-2 min-h-8">
                  {selectedTagObjs.map(t => (
                    <TagPill key={t.id} tag={t} onRemove={() => toggleTag(t.id)} />
                  ))}
                  {selectedTagObjs.length === 0 && <span className="text-text-muted text-sm font-body">No tags selected.</span>}
                </div>
              </div>
              <div className="section-divider" />
              <div>
                <label className="label">All Tags</label>
                <div className="flex flex-wrap gap-2">
                  {tags.map(t => (
                    <button
                      key={t.id}
                      onClick={() => toggleTag(t.id)}
                      className={`transition-opacity ${form.tag_ids.includes(t.id) ? 'opacity-50' : ''}`}
                    >
                      <TagPill tag={t} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── HANDLING LOG ── */}
          {openSection === 'log' && !isNew && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="font-heading text-2xl" style={{ color: 'var(--text)' }}>{formatMinutes(logData?.total_minutes ?? 0)}</span>
                <span className="text-text-muted text-xs font-body">total handling time</span>
              </div>
              <div className="section-divider" />
              {/* New entry */}
              <div className="space-y-3">
                <label className="label">Log New Session</label>
                <textarea
                  className="input h-20 resize-none"
                  placeholder="What was worked on / changed..."
                  value={newLogEntry.notes}
                  onChange={e => setNewLogEntry(n => ({ ...n, notes: e.target.value }))}
                />
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="label">Time Spent (minutes)</label>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      value={newLogEntry.time_spent_minutes}
                      onChange={e => setNewLogEntry(n => ({ ...n, time_spent_minutes: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                  <button
                    onClick={() => addLogMutation.mutate()}
                    disabled={addLogMutation.isPending}
                    className="px-4 py-2 bg-dark text-white text-sm font-heading uppercase hover:bg-primary transition-colors"
                  >
                    Log
                  </button>
                </div>
              </div>
              <div className="section-divider" />
              {/* History */}
              <div className="space-y-2">
                {logData?.log?.map((entry: HandlingLogEntry) => (
                  <div key={entry.id} className="border px-3 py-2 text-sm font-body" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex justify-between text-xs text-text-muted mb-1">
                      <span>{formatDate(entry.logged_at)}</span>
                      <span className="font-heading" style={{ color: 'var(--text)' }}>{formatMinutes(entry.time_spent_minutes)}</span>
                    </div>
                    {entry.notes && <p style={{ color: 'var(--text)' }}>{entry.notes}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── IMAGE ── */}
          {openSection === 'image' && (
            <div className="space-y-4">

              {/* Current image — only for existing vehicles */}
              {!isNew && vehicleImageSrc(vehicle!) && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="label mb-0">Current Image</label>
                    <span className="text-[10px] font-heading uppercase tracking-wide" style={{ color: 'var(--text-faint)' }}>
                      {vehicle!.image_key ? '📦 R2 Storage' : '🔗 External URL'}
                    </span>
                  </div>
                  <img
                    src={vehicleImageSrc(vehicle!)!}
                    alt="vehicle"
                    className="h-48 w-full object-contain border"
                    style={{ borderColor: 'var(--border)' }}
                  />
                </div>
              )}

              {/* Info banner for new vehicles */}
              {isNew && (
                <p className="text-xs font-body px-3 py-2 border" style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'var(--surface)' }}>
                  The image will be attached automatically when you click Create.
                </p>
              )}

              {/* Method tabs */}
              <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
                {(['file', 'clipboard', 'url'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => { setImageTab(tab); setImagePreview(null); setImageFile(null); }}
                    className={`px-4 py-2 text-xs font-heading uppercase tracking-wide border-b-2 transition-colors ${
                      imageTab === tab ? 'border-primary text-primary' : 'border-transparent'
                    }`}
                    style={imageTab !== tab ? { color: 'var(--text-muted)' } : undefined}
                  >
                    {tab === 'file' ? '📁 File' : tab === 'clipboard' ? '📋 Clipboard' : '🔗 URL'}
                  </button>
                ))}
              </div>

              {/* File Upload */}
              {imageTab === 'file' && (
                <div className="space-y-3">
                  <p className="text-xs font-body" style={{ color: 'var(--text-muted)' }}>
                    Select an image file — uploaded to R2 storage.
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    className="block w-full text-sm font-body file:mr-3 file:py-1.5 file:px-3 file:border-0 file:bg-dark file:text-white file:font-heading file:uppercase file:text-xs cursor-pointer"
                    style={{ color: 'var(--text-muted)' }}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setImageFile(file);
                      const reader = new FileReader();
                      reader.onload = ev => setImagePreview(ev.target?.result as string);
                      reader.readAsDataURL(file);
                    }}
                  />
                  {imagePreview && (
                    <img src={imagePreview} alt="preview" className="h-40 object-contain border" style={{ borderColor: 'var(--border)' }} />
                  )}
                  {imageFile && (
                    <button
                      onClick={() => uploadMutation.mutate()}
                      disabled={uploadMutation.isPending}
                      className="px-4 py-2 bg-primary text-white text-sm font-heading uppercase hover:bg-primary-dark transition-colors"
                    >
                      {uploadMutation.isPending ? 'Uploading…' : 'Upload to R2'}
                    </button>
                  )}
                </div>
              )}

              {/* Clipboard Paste */}
              {imageTab === 'clipboard' && (
                <div
                  className="space-y-3"
                  onPaste={e => {
                    const items = e.clipboardData.items;
                    for (let i = 0; i < items.length; i++) {
                      if (items[i].type.startsWith('image/')) {
                        const file = items[i].getAsFile();
                        if (file) {
                          setImageFile(file);
                          const reader = new FileReader();
                          reader.onload = ev => setImagePreview(ev.target?.result as string);
                          reader.readAsDataURL(file);
                        }
                        return;
                      }
                    }
                  }}
                >
                  <p className="text-xs font-body" style={{ color: 'var(--text-muted)' }}>
                    Copy an image to your clipboard, then paste it below — uploaded to R2 storage.
                  </p>
                  {/* Paste zone */}
                  <div
                    tabIndex={0}
                    className="border-2 border-dashed px-6 py-8 text-center cursor-pointer transition-colors focus:outline-none"
                    style={{ borderColor: imagePreview ? 'var(--border-strong)' : 'var(--border)' }}
                    onClick={async () => {
                      try {
                        const clipItems = await navigator.clipboard.read();
                        for (const item of clipItems) {
                          const imgType = item.types.find(t => t.startsWith('image/'));
                          if (imgType) {
                            const blob = await item.getType(imgType);
                            const ext  = imgType.split('/')[1] ?? 'png';
                            const file = new File([blob], `clipboard-${Date.now()}.${ext}`, { type: imgType });
                            setImageFile(file);
                            const reader = new FileReader();
                            reader.onload = ev => setImagePreview(ev.target?.result as string);
                            reader.readAsDataURL(file);
                            return;
                          }
                        }
                      } catch {
                        // Clipboard API blocked — user can still press Ctrl+V
                      }
                    }}
                    onKeyDown={e => e.key === 'Enter' && (e.currentTarget as HTMLDivElement).click()}
                  >
                    {imagePreview ? (
                      <img src={imagePreview} alt="preview" className="max-h-36 mx-auto object-contain" />
                    ) : (
                      <span className="font-body text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                        Click here then press{' '}
                        <kbd className="px-1 py-0.5 text-[11px] border font-mono" style={{ borderColor: 'var(--border-strong)', color: 'var(--text)' }}>
                          Ctrl+V
                        </kbd>
                        {' '}to paste,<br />
                        or click to read from clipboard
                      </span>
                    )}
                  </div>
                  {imageFile && (
                    <button
                      onClick={() => uploadMutation.mutate()}
                      disabled={uploadMutation.isPending}
                      className="px-4 py-2 bg-primary text-white text-sm font-heading uppercase hover:bg-primary-dark transition-colors"
                    >
                      {uploadMutation.isPending ? 'Uploading…' : 'Upload to R2'}
                    </button>
                  )}
                </div>
              )}

              {/* External URL */}
              {imageTab === 'url' && (
                <div className="space-y-3">
                  <p className="text-xs font-body" style={{ color: 'var(--text-muted)' }}>
                    Paste a direct image URL from a website or GitHub repo. Nothing is uploaded — the image loads from its source.
                  </p>
                  <input
                    className="input"
                    placeholder="https://raw.githubusercontent.com/..."
                    value={imageUrlInput}
                    onChange={e => setImageUrlInput(e.target.value)}
                  />
                  {imageUrlInput.trim() && (
                    <img
                      src={imageUrlInput.trim()}
                      alt="preview"
                      className="h-36 object-contain border"
                      style={{ borderColor: 'var(--border)' }}
                      onError={e  => { e.currentTarget.style.display = 'none'; }}
                      onLoad={e   => { e.currentTarget.style.display = ''; }}
                    />
                  )}
                  <button
                    onClick={() => setImageUrlMutation.mutate()}
                    disabled={!imageUrlInput.trim() || setImageUrlMutation.isPending}
                    className="px-4 py-2 bg-primary text-white text-sm font-heading uppercase hover:bg-primary-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {setImageUrlMutation.isPending ? 'Saving…' : 'Save URL'}
                  </button>
                </div>
              )}

            </div>
          )}

          {/* ── AUDIT LOG ── */}
          {openSection === 'audit' && !isNew && (
            <div className="space-y-2">
              {auditData?.log?.length === 0 && <p className="text-text-muted text-sm font-body">No changes recorded yet.</p>}
              {auditData?.log?.map((entry: AuditLogEntry) => {
                let changes: Record<string, { old: unknown; new: unknown }> = {};
                try { changes = JSON.parse(entry.changes); } catch { /* */ }
                return (
                  <div key={entry.id} className="border px-3 py-2.5 text-sm font-body" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex justify-between text-xs text-text-muted mb-1">
                      <span className="font-heading" style={{ color: 'var(--text)' }}>{entry.changed_by}</span>
                      <span>{formatDate(entry.changed_at)}</span>
                    </div>
                    {entry.notes && <p className="text-text-muted text-xs mb-1 italic">{entry.notes}</p>}
                    <div className="space-y-0.5">
                      {Object.entries(changes).map(([field, { old: o, new: n }]) => (
                        <p key={field} className="text-xs">
                          <span className="font-heading uppercase text-text-muted">{field}</span>
                          {' '}
                          <span className="line-through text-text-muted">{String(o ?? '—')}</span>
                          {' → '}
                          <span className="font-semibold" style={{ color: 'var(--text)' }}>{String(n ?? '—')}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-5 py-3 flex items-center justify-between flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
          <div className="flex gap-2">
            {!isNew && (
              deleteConfirm ? (
                <>
                  <span className="text-primary text-xs font-body self-center">Delete this vehicle?</span>
                  <button
                    ref={deleteRef}
                    onClick={() => deleteMutation.mutate()}
                    className="px-3 py-1.5 bg-primary text-white text-xs font-heading uppercase hover:bg-primary-dark transition-colors"
                  >
                    Confirm Delete
                  </button>
                  <button onClick={() => setDeleteConfirm(false)} className="px-3 py-1.5 border text-xs font-heading uppercase transition-colors" style={{ borderColor: 'var(--border-strong)', color: 'var(--text)' }}>
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setDeleteConfirm(true)}
                  className="px-3 py-1.5 border border-primary text-primary text-xs font-heading uppercase hover:bg-primary hover:text-white transition-colors"
                >
                  Delete
                </button>
              )
            )}
          </div>
          <div className="flex gap-2 items-center">
            <input
              className="input text-xs py-1.5 w-48"
              placeholder="Audit note (optional)"
              value={auditNote}
              onChange={e => setAuditNote(e.target.value)}
            />
            <button onClick={requestClose} className="px-4 py-1.5 border text-sm font-heading uppercase transition-colors" style={{ borderColor: 'var(--border-strong)', color: 'var(--text)' }}>
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saveMutation.isPending}
              className="px-5 py-1.5 bg-dark text-white text-sm font-heading uppercase hover:bg-primary transition-colors"
            >
              {saveMutation.isPending ? 'Saving…' : (isNew ? 'Create' : 'Save')}
            </button>
          </div>
        </div>

        {/* ── Unsaved-changes confirmation ── */}
        {showCloseConfirm && (
          <div
            className="absolute inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
          >
            <div
              ref={confirmRef}
              className="shadow-modal flex flex-col overflow-hidden"
              style={{
                background: 'var(--card)',
                border: '1px solid var(--border-strong)',
                width: 320,
                willChange: 'transform, opacity',
              }}
            >
              {/* Dialog header */}
              <div className="bg-dark px-5 py-3 flex items-center gap-3">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#C8102E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span className="font-heading text-white text-sm uppercase tracking-wide">Unsaved Changes</span>
              </div>

              {/* Dialog body */}
              <div className="px-5 py-4 space-y-4">
                <p className="font-body text-sm" style={{ color: 'var(--text-muted)' }}>
                  You have unsaved changes. Closing now will discard them.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={confirmDiscard}
                    className="flex-1 px-4 py-2 border border-primary text-primary text-sm font-heading uppercase hover:bg-primary hover:text-white transition-colors"
                  >
                    Discard
                  </button>
                  <button
                    onClick={dismissConfirm}
                    className="flex-1 px-4 py-2 bg-dark text-white text-sm font-heading uppercase hover:bg-primary transition-colors"
                  >
                    Keep Editing
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
